import React, { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useGoogleLogin } from "@react-oauth/google";
import Header from "../../components/Header";
import ProductCard from "../../components/ProductCard";
import api from "../../utils/api";
import { validateFile, validateUsername } from "../../utils/validation";
import { getProfileImageUrl, getImageUrl } from "../../utils/imageUtils";
import { getCurrentLocation, reverseGeocode } from "../../utils/locationUtils";
import { getCartItems, addToCart } from "../../utils/cartUtils";
import FloatingCart from "../../components/FloatingCart";
import CartDrawer from "../../components/CartDrawer";
import { usePopup } from "../../context/PopupContext";
import {
  FaMapMarkerAlt,
  FaPhone,
  FaEnvelope,
  FaStore,
  FaLeaf,
  FaBox,
  FaClipboardList,
  FaStar,
  FaCalendarAlt,
  FaCamera,
  FaCheck,
  FaTimes,
  FaEdit,
  FaComment,
  FaCoins,
} from "react-icons/fa";
import ProfileImageCropModal from "../../components/ProfileImageCropModal";
import "./FarmerPublicProfile.css";

const FarmerPublicProfile = () => {
  const { t } = useTranslation();
  const { username } = useParams();
  const navigate = useNavigate();
  const { showToast, showConfirm } = usePopup();
  const [farmer, setFarmer] = useState(null);

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [profileImagePreview, setProfileImagePreview] = useState(null);
  const [isEditingInfo, setIsEditingInfo] = useState(false);
  const [editFormData, setEditFormData] = useState({
    username: "",
    name: "",
    email: "",
    phone: "",
    city: "",
    district: "",
    province: "",
    address: "",
    municipality: "",
    farmerType: "sellingFromHome",
    farmName: "",
    paymentMethod: "",
    khaltiAccountType: "",
    khaltiAccountId: "",
    bio: "",
    specialties: [],
  });
  const [specialtyInput, setSpecialtyInput] = useState("");
  const [farmPhotoFile, setFarmPhotoFile] = useState(null);
  const [farmPhotoPreview, setFarmPhotoPreview] = useState(null);
  const [savingInfo, setSavingInfo] = useState(false);
  const [coverImageFile, setCoverImageFile] = useState(null);
  const [coverImagePreview, setCoverImagePreview] = useState(null);
  const [uploadingCover, setUploadingCover] = useState(false);
  const [cropModalOpen, setCropModalOpen] = useState(false);
  const [cropImageSrc, setCropImageSrc] = useState(null);
  const [detectingLocation, setDetectingLocation] = useState(false);
  const [activeTab, setActiveTab] = useState("profile");
  const [exportingData, setExportingData] = useState(false);
  const [deletingAccount, setDeletingAccount] = useState(false);
  const [cartItems, setCartItems] = useState(getCartItems());
  const [isCartOpen, setIsCartOpen] = useState(false);

  const nepaleseProvinces = [
    "Province 1",
    "Province 2",
    "Bagmati",
    "Gandaki",
    "Lumbini",
    "Karnali",
    "Sudurpashchim",
  ];
  const districtsByProvince = {
    "Province 1": [
      "Bhojpur",
      "Dhankuta",
      "Ilam",
      "Jhapa",
      "Khotang",
      "Morang",
      "Okhaldhunga",
      "Panchthar",
      "Sankhuwasabha",
      "Solukhumbu",
      "Sunsari",
      "Taplejung",
      "Terhathum",
      "Udayapur",
    ],
    "Province 2": [
      "Bara",
      "Dhanusha",
      "Mahottari",
      "Parsa",
      "Rautahat",
      "Saptari",
      "Sarlahi",
      "Siraha",
    ],
    Bagmati: [
      "Bhaktapur",
      "Chitwan",
      "Dhading",
      "Dolakha",
      "Kathmandu",
      "Kavrepalanchok",
      "Lalitpur",
      "Makwanpur",
      "Nuwakot",
      "Ramechhap",
      "Rasuwa",
      "Sindhuli",
      "Sindhupalchok",
    ],
    Gandaki: [
      "Baglung",
      "Gorkha",
      "Kaski",
      "Lamjung",
      "Manang",
      "Mustang",
      "Myagdi",
      "Nawalpur",
      "Parbat",
      "Syangja",
      "Tanahun",
    ],
    Lumbini: [
      "Arghakhanchi",
      "Banke",
      "Bardiya",
      "Dang",
      "Gulmi",
      "Kapilvastu",
      "Palpa",
      "Parasi",
      "Pyuthan",
      "Rolpa",
      "Rukum",
      "Rupandehi",
    ],
    Karnali: [
      "Dailekh",
      "Dolpa",
      "Humla",
      "Jajarkot",
      "Jumla",
      "Kalikot",
      "Mugu",
      "Rukum",
      "Salyan",
      "Surkhet",
    ],
    Sudurpashchim: [
      "Achham",
      "Baitadi",
      "Bajhang",
      "Bajura",
      "Dadeldhura",
      "Darchula",
      "Doti",
      "Kailali",
      "Kanchanpur",
    ],
  };

  useEffect(() => {
    const userData = localStorage.getItem("user");
    if (userData) {
      try {
        setUser(JSON.parse(userData));
      } catch (error) {
        console.error("Error parsing user data:", error);
      }
    }

    fetchFarmerProfile();

    const syncCart = () => setCartItems(getCartItems());
    window.addEventListener("cartUpdated", syncCart);
    window.addEventListener("storage", syncCart);
    return () => {
      window.removeEventListener("cartUpdated", syncCart);
      window.removeEventListener("storage", syncCart);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [username]);

  // Initialize edit form data when farmer data is loaded
  useEffect(() => {
    if (farmer) {
      setEditFormData({
        username: farmer.username || "",
        name: farmer.name || "",
        email: farmer.email || "",
        phone: farmer.phone || "",
        city: farmer.address?.city || farmer.city || "",
        district: farmer.district || farmer.address?.district || "",
        province: farmer.province || farmer.address?.province || "",
        address:
          farmer.address?.street ||
          (typeof farmer.address === "string" ? farmer.address : "") ||
          "",
        municipality: farmer.municipality || farmer.address?.municipality || "",
        farmerType: farmer.farmerType || "sellingFromHome",
        farmName: farmer.farmName || "",
        paymentMethod: farmer.paymentMethod || "",
        khaltiAccountType: farmer.khaltiAccountType || "",
        khaltiAccountId: farmer.khaltiAccountId || "",
        bio: farmer.bio || "",
        specialties: farmer.specialties || [],
      });
    }
  }, [farmer]);

  const fetchFarmerProfile = async () => {
    setLoading(true);
    try {
      // Fetch farmer by username from API, or current user's profile if no username
      let farmerData;
      try {
        if (username) {
          // Fetch by username from URL
          const response = await api.get(`/users/${username}`);
          farmerData = response.data;
        } else {
          // Fetch current user's profile
          const response = await api.get("/users/profile");
          farmerData = response.data;
          // If we got the profile and it has a username, update the URL
          if (farmerData && farmerData.username) {
            // Keep the edit profile route, don't redirect
          }
        }
      } catch (error) {
        console.error("Error fetching farmer:", error);
        farmerData = null;
      }

      if (farmerData && farmerData._id) {
        // Validate that the user is actually a farmer
        if (farmerData.role !== "farmer") {
          console.error("User is not a farmer:", farmerData.role);
          setFarmer(null);
          setProducts([]);
          setLoading(false);
          return;
        }

        // Fetch products for this farmer
        let productsData = [];
        try {
          const productsResponse = await api.get(
            `/products?farmer=${farmerData._id}`,
          );
          productsData = productsResponse.data || [];
        } catch (error) {
          console.error("Error fetching products:", error);
        }

        // Fetch order count for this farmer
        let totalOrders = 0;
        try {
          const ordersResponse = await api.get("/orders");
          const allOrders = ordersResponse.data || [];
          totalOrders = allOrders.filter(
            (o) =>
              (o.farmer?._id || o.farmer)?.toString() ===
              farmerData._id.toString(),
          ).length;
        } catch (error) {
          console.error("Error fetching orders:", error);
        }

        // Fetch reviews to calculate rating - always fetch fresh from database
        let rating = 0;
        let totalReviews = 0;
        try {
          const reviewsResponse = await api.get(
            `/reviews/farmer/${farmerData._id}`,
          );
          const reviews = reviewsResponse.data || [];
          totalReviews = reviews.length;
          if (reviews.length > 0) {
            const sum = reviews.reduce(
              (acc, review) => acc + (review.rating || 0),
              0,
            );
            rating = sum / reviews.length;
          }
        } catch (error) {
          console.error("Error fetching reviews for farmer profile:", error);
          // If endpoint doesn't exist, try to calculate from products
          try {
            let allReviews = [];
            for (const product of productsData) {
              try {
                const productReviewsResponse = await api.get(
                  `/reviews/product/${product._id}`,
                );
                allReviews = allReviews.concat(
                  productReviewsResponse.data || [],
                );
              } catch (e) {
                // Continue if product has no reviews
              }
            }
            totalReviews = allReviews.length;
            if (allReviews.length > 0) {
              const sum = allReviews.reduce(
                (acc, review) => acc + (review.rating || 0),
                0,
              );
              rating = sum / allReviews.length;
            }
          } catch (e) {
            console.error("Error calculating reviews from products:", e);
          }
        }

        // Use API data
        const farmerToUse = {
          ...farmerData,
          verified: farmerData.verified || false,
          role: farmerData.role || "farmer",
          district: farmerData.address?.district || farmerData.district,
          province: farmerData.address?.province || farmerData.province,
          address: farmerData.address?.street || farmerData.address,
          municipality:
            farmerData.address?.municipality || farmerData.municipality,
          totalProducts: productsData.length,
          totalOrders: totalOrders,
          rating: Math.round(rating * 10) / 10, // Round to 1 decimal
          totalReviews: totalReviews,
          joinedDate: farmerData.createdAt || new Date(),
          bio: farmerData.bio || "",
          specialties: farmerData.specialties || [],
        };
        setFarmer(farmerToUse);
        setProducts(productsData);
      } else {
        // No farmer found
        setFarmer(null);
        setProducts([]);
      }

      setLoading(false);
    } catch (error) {
      console.error("Error fetching farmer profile:", error);
      setLoading(false);
    }
  };

  const getProfileImage = () => {
    if (profileImagePreview) {
      return profileImagePreview;
    }
    if (farmer?.profileImage) {
      if (farmer.profileImage.startsWith("http")) {
        return farmer.profileImage;
      }
      return getProfileImageUrl(farmer.profileImage);
    }
    return "/images/deafultpic.png";
  };

  const getCoverImage = () => {
    if (coverImagePreview) return coverImagePreview;
    if (farmer?.coverImage) {
      return farmer.coverImage.startsWith("http")
        ? farmer.coverImage
        : getImageUrl(farmer.coverImage);
    }
    return null;
  };

  const handleCoverImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const validation = validateFile(file, [
      "image/png",
      "image/jpeg",
      "image/jpg",
    ]);
    if (!validation.isValid) {
      showToast(validation.error, "error");
      return;
    }
    setCoverImageFile(file);
    const reader = new FileReader();
    reader.onloadend = () => setCoverImagePreview(reader.result);
    reader.readAsDataURL(file);
  };

  const handleCoverImageUpload = async () => {
    if (!coverImageFile) return;
    setUploadingCover(true);
    try {
      const formData = new FormData();
      formData.append("coverImage", coverImageFile);
      // Do not set Content-Type: let axios set multipart/form-data with boundary
      const response = await api.put("/users/profile", formData);
      setFarmer((prev) => ({ ...prev, coverImage: response.data.coverImage }));
      setCoverImageFile(null);
      setCoverImagePreview(null);
      showToast(t("farmerPublicProfile.coverPhotoUpdated"), "success");
    } catch (err) {
      console.error("Cover upload error:", err?.response?.data || err);
      const msg =
        err.response?.data?.message ||
        err.message ||
        t("farmerPublicProfile.updateCoverFailed");
      showToast(msg, "error");
    } finally {
      setUploadingCover(false);
    }
  };

  const handleCoverCancel = () => {
    setCoverImageFile(null);
    setCoverImagePreview(null);
  };

  // Check if logged-in user is viewing their own profile
  const isOwnProfile =
    user && farmer && (user.id === farmer._id || user._id === farmer._id);

  const handleProfileImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const validation = validateFile(file, [
      "image/png",
      "image/jpeg",
      "image/jpg",
    ]);
    if (!validation.isValid) {
      showToast(validation.error, "error");
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setCropImageSrc(reader.result);
      setCropModalOpen(true);
    };
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  const uploadProfileImageFile = async (file) => {
    try {
      const formData = new FormData();
      formData.append("profileImage", file);

      const response = await api.put("/users/profile", formData);

      setFarmer((prev) => ({
        ...prev,
        profileImage: response.data.profileImage,
      }));

      const userData = JSON.parse(localStorage.getItem("user"));
      userData.profileImage = response.data.profileImage;
      localStorage.setItem("user", JSON.stringify(userData));
      setUser(userData);

      window.dispatchEvent(new Event("storage"));
      window.dispatchEvent(new Event("userUpdated"));

      setProfileImagePreview(null);
      showToast(t("farmerPublicProfile.profilePictureUpdated"), "success");
    } catch (error) {
      console.error("Error uploading profile image:", error);
      showToast(
        error.response?.data?.message ||
          t("farmerPublicProfile.updatePictureFailed"),
        "error",
      );
    }
  };

  const handleCropSave = (blob) => {
    const file = new File([blob], "profile.jpg", { type: "image/jpeg" });
    setCropModalOpen(false);
    setCropImageSrc(null);
    uploadProfileImageFile(file);
  };

  const handleCropCancel = () => {
    setCropModalOpen(false);
    setCropImageSrc(null);
  };

  const downloadBackupFile = (data) => {
    const blob = new Blob([JSON.stringify(data, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `taja-bazzar-backup-${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleDownloadBackup = async () => {
    setExportingData(true);
    try {
      const { data } = await api.get("/users/export-data");
      downloadBackupFile(data);
      showToast(t("farmerPublicProfile.backupDownloaded"), "success");
    } catch (err) {
      showToast(
        err.response?.data?.message || t("farmerPublicProfile.exportFailed"),
        "error",
      );
    } finally {
      setExportingData(false);
    }
  };

  const uploadBackupToDrive = async (googleAccessToken) => {
    setExportingData(true);
    try {
      const { data } = await api.post("/users/backup-to-drive", {
        googleAccessToken,
      });
      showToast(
        data.message || t("farmerPublicProfile.backupUploaded"),
        "success",
      );
      if (data.webViewLink) {
        window.open(data.webViewLink, "_blank");
      }
    } catch (err) {
      showToast(
        err.response?.data?.message ||
          t("farmerPublicProfile.backupUploadFailed"),
        "error",
      );
    } finally {
      setExportingData(false);
    }
  };

  const googleDriveLogin = useGoogleLogin({
    scope: "https://www.googleapis.com/auth/drive.file",
    onSuccess: (tokenResponse) => {
      uploadBackupToDrive(tokenResponse.access_token);
    },
    onError: () => {
      setExportingData(false);
      showToast(t("farmerPublicProfile.googleSignInCancelled"), "error");
    },
  });

  const handleBackupToGoogleDrive = () => {
    setExportingData(true);
    googleDriveLogin();
  };

  const handleDeleteAccount = () => {
    showConfirm({
      title: t("farmerPublicProfile.backupDataFirst"),
      message: t("farmerPublicProfile.backupMessage"),
      confirmLabel: t("farmerPublicProfile.yesContinueDelete"),
      cancelLabel: t("farmerPublicProfile.noBackupFirst"),
      onConfirm: () => {
        showConfirm({
          title: t("farmerPublicProfile.permanentlyDeleteAccount"),
          message: t("farmerPublicProfile.deleteWarning"),
          confirmLabel: t("farmerPublicProfile.deleteMyAccount"),
          cancelLabel: "Cancel",
          onConfirm: async () => {
            setDeletingAccount(true);
            try {
              await api.delete("/users/account");
              showToast(t("farmerPublicProfile.accountDeleted"), "success");
              localStorage.removeItem("token");
              localStorage.removeItem("user");
              navigate("/");
              window.location.reload();
            } catch (err) {
              showToast(
                err.response?.data?.message ||
                  t("farmerPublicProfile.deleteFailed"),
                "error",
              );
            } finally {
              setDeletingAccount(false);
            }
          },
        });
      },
    });
  };

  const handleEditInfoChange = (e) => {
    const { name, value } = e.target;
    setEditFormData((prev) => ({
      ...prev,
      [name]: value,
      ...(name === "province" ? { district: "", municipality: "" } : {}),
    }));
  };

  const handleUseMyLocation = async () => {
    setDetectingLocation(true);
    try {
      const { latitude, longitude } = await getCurrentLocation();
      const addr = await reverseGeocode(latitude, longitude);

      // Only use province if it's in our list (so the dropdown can show it)
      const provinceValue =
        addr.province && nepaleseProvinces.includes(addr.province)
          ? addr.province
          : "";

      // Match district to dropdown option so the select displays correctly
      let districtValue = addr.district || "";
      if (
        provinceValue &&
        districtValue &&
        districtsByProvince[provinceValue]
      ) {
        const options = districtsByProvince[provinceValue];
        const match = options.find(
          (d) =>
            d.toLowerCase() === districtValue.toLowerCase() ||
            districtValue.toLowerCase().includes(d.toLowerCase()),
        );
        if (match) districtValue = match;
      }

      setEditFormData((prev) => ({
        ...prev,
        city: addr.city || prev.city,
        province: provinceValue || prev.province,
        district: districtValue || prev.district,
        municipality: addr.municipality || prev.municipality,
        address: addr.address || prev.address,
      }));
      showToast(t("farmerPublicProfile.locationUpdated"), "success");
    } catch (err) {
      console.error("Use location error:", err);
      const msg = err.message || t("farmerPublicProfile.couldNotGetLocation");
      showToast(msg, "error");
    } finally {
      setDetectingLocation(false);
    }
  };

  const handleFarmPhotoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      showToast("Please select an image file", "error");
      return;
    }
    setFarmPhotoFile(file);
    const reader = new FileReader();
    reader.onloadend = () => setFarmPhotoPreview(reader.result);
    reader.readAsDataURL(file);
  };

  const handleSaveInfo = async () => {
    setSavingInfo(true);
    try {
      // Validate username if it changed
      if (editFormData.username && editFormData.username !== farmer.username) {
        const usernameValidation = validateUsername(
          editFormData.username.toLowerCase(),
        );
        if (!usernameValidation.isValid) {
          showToast(usernameValidation.error, "error");
          setSavingInfo(false);
          return;
        }
      }

      const newUsername = editFormData.username
        ? String(editFormData.username).toLowerCase().trim()
        : "";
      const currentUsername = farmer.username
        ? String(farmer.username).toLowerCase().trim()
        : "";

      const addressPayload = {
        city: editFormData.city,
        district: editFormData.district,
        province: editFormData.province,
        street: editFormData.address,
        municipality: editFormData.municipality,
      };

      let response;
      if (farmPhotoFile) {
        const formData = new FormData();
        formData.append("name", editFormData.name);
        formData.append("email", editFormData.email);
        formData.append("phone", editFormData.phone || "");
        formData.append("address", JSON.stringify(addressPayload));
        formData.append(
          "farmerType",
          editFormData.farmerType || "sellingFromHome",
        );
        if (newUsername && newUsername !== currentUsername)
          formData.append("username", newUsername);
        if (editFormData.farmerType === "registeredFarm") {
          if (editFormData.farmName)
            formData.append("farmName", editFormData.farmName);
          formData.append("farmPhoto", farmPhotoFile);
        }
        if (editFormData.paymentMethod)
          formData.append("paymentMethod", editFormData.paymentMethod);
        if (editFormData.paymentMethod === "khalti") {
          if (editFormData.khaltiAccountType)
            formData.append(
              "khaltiAccountType",
              editFormData.khaltiAccountType,
            );
          if (editFormData.khaltiAccountId !== undefined)
            formData.append("khaltiAccountId", editFormData.khaltiAccountId);
        }
        if (editFormData.bio !== undefined)
          formData.append("bio", editFormData.bio || "");
        formData.append(
          "specialties",
          JSON.stringify(editFormData.specialties || []),
        );
        response = await api.put("/users/profile", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      } else {
        const updateData = {
          name: editFormData.name,
          email: editFormData.email,
          phone: editFormData.phone,
          address: addressPayload,
          farmerType: editFormData.farmerType || "sellingFromHome",
        };
        if (newUsername && newUsername !== currentUsername)
          updateData.username = newUsername;
        if (editFormData.farmerType === "registeredFarm") {
          updateData.farmName = editFormData.farmName || "";
        }
        updateData.paymentMethod = editFormData.paymentMethod || "";
        if (editFormData.paymentMethod === "khalti") {
          updateData.khaltiAccountType = editFormData.khaltiAccountType || null;
          updateData.khaltiAccountId = editFormData.khaltiAccountId || null;
        }
        if (editFormData.bio !== undefined)
          updateData.bio = editFormData.bio || "";
        updateData.specialties = editFormData.specialties || [];
        response = await api.put("/users/profile", updateData);
      }

      setFarmPhotoFile(null);
      setFarmPhotoPreview(null);

      // Update farmer state
      setFarmer((prev) => ({
        ...prev,
        username: response.data.username || prev.username,
        name: response.data.name,
        email: response.data.email,
        phone: response.data.phone,
        address: response.data.address || prev.address,
        district: response.data.address?.district ?? editFormData.district,
        province: response.data.address?.province ?? editFormData.province,
        municipality:
          response.data.address?.municipality ?? editFormData.municipality,
        farmerType: response.data.farmerType ?? editFormData.farmerType,
        farmName: response.data.farmName ?? editFormData.farmName,
        farmPhoto: response.data.farmPhoto ?? prev.farmPhoto,
        paymentMethod:
          response.data.paymentMethod ?? editFormData.paymentMethod,
        khaltiAccountType:
          response.data.khaltiAccountType ?? editFormData.khaltiAccountType,
        khaltiAccountId:
          response.data.khaltiAccountId ?? editFormData.khaltiAccountId,
        bio: response.data.bio ?? editFormData.bio ?? prev.bio,
        specialties: response.data.specialties ?? prev.specialties,
      }));

      // If username was changed, update localStorage and redirect
      if (
        response.data.username &&
        response.data.username !== farmer.username
      ) {
        // Update user in localStorage with new username
        const userData = JSON.parse(localStorage.getItem("user"));
        userData.username = response.data.username;
        localStorage.setItem("user", JSON.stringify(userData));

        // Redirect to edit profile page (not public view)
        window.location.href = "/farmer/profile";
        return;
      }

      // Update user in localStorage
      const userData = JSON.parse(localStorage.getItem("user"));
      if (userData) {
        userData.name = response.data.name;
        userData.email = response.data.email;
        userData.phone = response.data.phone;
        if (response.data.username) {
          userData.username = response.data.username;
        }
        localStorage.setItem("user", JSON.stringify(userData));
        setUser(userData);
      }

      setIsEditingInfo(false);
      showToast(t("farmerPublicProfile.profileUpdated"), "success");
    } catch (error) {
      console.error("Error updating profile:", error);
      console.error("Error response:", error.response?.data);
      const errorMessage =
        error.response?.data?.message || t("farmerPublicProfile.updateFailed");
      showToast(errorMessage, "error");
      // If username change failed, reset username in form and reload username change info
      if (
        errorMessage.includes("username") ||
        errorMessage.includes("Username")
      ) {
        setEditFormData((prev) => ({
          ...prev,
          username: farmer.username,
        }));
      }
    } finally {
      setSavingInfo(false);
    }
  };

  const handleCancelInfoEdit = () => {
    setFarmPhotoFile(null);
    setFarmPhotoPreview(null);
    setSpecialtyInput("");
    if (farmer) {
      setEditFormData({
        username: farmer.username || "",
        name: farmer.name || "",
        email: farmer.email || "",
        phone: farmer.phone || "",
        city: farmer.address?.city || "",
        district: farmer.district || farmer.address?.district || "",
        province: farmer.province || farmer.address?.province || "",
        address: farmer.address?.street || "",
        municipality: farmer.municipality || farmer.address?.municipality || "",
        farmerType: farmer.farmerType || "sellingFromHome",
        farmName: farmer.farmName || "",
        paymentMethod: farmer.paymentMethod || "",
        khaltiAccountType: farmer.khaltiAccountType || "",
        khaltiAccountId: farmer.khaltiAccountId || "",
        bio: farmer.bio || "",
        specialties: farmer.specialties || [],
      });
    }
    setIsEditingInfo(false);
  };

  const handleAddToCart = (product) => {
    if (!user || user.role !== "consumer") {
      navigate("/login");
      return;
    }
    const updated = addToCart(product, 1);
    setCartItems(updated);
  };

  if (loading) {
    return (
      <div className="farmer-public-profile-page">
        <Header />
        <div className="fpp-loading">
          <div className="fpp-spinner" />
          <p>{t("farmerPublicProfile.loading")}</p>
        </div>
      </div>
    );
  }

  if (!farmer) {
    return (
      <div className="farmer-public-profile-page">
        <Header />
        <div className="fpp-empty">
          <h2>{t("farmerPublicProfile.notFound")}</h2>
          <Link to="/farmers" className="fpp-btn fpp-btn-primary">
            {t("farmerPublicProfile.backToFarmers")}
          </Link>
        </div>
      </div>
    );
  }

  const specialties = Array.isArray(farmer.specialties)
    ? farmer.specialties
    : [];

  return (
    <div className="farmer-public-profile-page">
      <Header />

      <div className="farmer-public-profile-container">
        {/* Hero / Cover + Avatar */}
        <div className="fpp-hero">
          <div
            className="fpp-cover"
            style={
              getCoverImage()
                ? { backgroundImage: `url(${getCoverImage()})` }
                : {}
            }
          >
            {isOwnProfile && (
              <div
                className={`fpp-cover-edit${coverImageFile ? " fpp-cover-edit-active" : ""}`}
              >
                {!coverImageFile ? (
                  <>
                    <input
                      type="file"
                      id="fpp-cover-input"
                      accept="image/png,image/jpeg,image/jpg"
                      onChange={handleCoverImageChange}
                      className="fpp-hidden-input"
                    />
                    <label
                      htmlFor="fpp-cover-input"
                      className="fpp-cover-edit-btn"
                    >
                      <FaCamera /> {t("farmerPublicProfile.changeCover")}
                    </label>
                  </>
                ) : (
                  <div className="fpp-cover-edit-actions">
                    <button
                      type="button"
                      className="fpp-btn fpp-cover-confirm"
                      onClick={handleCoverImageUpload}
                      disabled={uploadingCover}
                    >
                      {uploadingCover ? (
                        <span className="fpp-spinner-small" />
                      ) : (
                        <>
                          <FaCheck /> Save
                        </>
                      )}
                    </button>
                    <button
                      type="button"
                      className="fpp-btn fpp-cover-cancel"
                      onClick={handleCoverCancel}
                      disabled={uploadingCover}
                    >
                      <FaTimes /> Cancel
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
          <div className="fpp-hero-inner">
            <div className="fpp-avatar-wrap">
              <div className="fpp-avatar-box">
                <img
                  src={getProfileImage()}
                  alt={farmer.name}
                  className="fpp-avatar"
                  referrerPolicy="no-referrer"
                />
                {isOwnProfile && (
                  <div className="fpp-avatar-edit-overlay">
                    <input
                      type="file"
                      id="farmerProfileImageInput"
                      accept="image/png,image/jpeg,image/jpg"
                      onChange={handleProfileImageChange}
                      className="fpp-hidden-input"
                    />
                    <label
                      htmlFor="farmerProfileImageInput"
                      className="fpp-avatar-edit-btn"
                      title="Change Photo"
                    >
                      <FaCamera />
                    </label>
                  </div>
                )}
                {farmer.verified && (
                  <span
                    className="fpp-avatar-verified"
                    title={t("farmerPublicProfile.verifiedFarmer")}
                  >
                    <FaCheck />
                  </span>
                )}
              </div>
              {!farmer.verified && farmer.role === "farmer" && (
                <span
                  className="fpp-badge fpp-badge-pending"
                  title={t("farmerPublicProfile.adminValidationPending")}
                >
                  {t("farmerPublicProfile.adminValidationPending")}
                </span>
              )}
              {!isEditingInfo && (
                <>
                  <h1 className="fpp-hero-name">{farmer.name}</h1>
                  <div className="fpp-hero-rating">
                    <FaStar className="fpp-hero-rating-star" />
                    <span className="fpp-hero-rating-value">
                      {farmer.rating}
                    </span>
                    <span className="fpp-hero-rating-count">
                      ({farmer.totalReviews} reviews)
                    </span>
                  </div>
                  {!isOwnProfile && user?.role === "consumer" && (
                    <Link
                      to={`/messages?farmer=${farmer._id}`}
                      className="fpp-hero-msg-btn"
                    >
                      <FaComment /> {t("farmerPublicProfile.messageFarmer")}
                    </Link>
                  )}
                  {!isOwnProfile && !user && (
                    <Link to="/login" className="fpp-hero-msg-btn">
                      <FaComment /> {t("farmerPublicProfile.loginToMessage")}
                    </Link>
                  )}
                </>
              )}
            </div>
          </div>
        </div>

        {/* Identity card: edit fields, rating, actions */}
        <div className="fpp-card fpp-identity">
          <div className="fpp-identity-top">
            <div className="fpp-identity-head">
              {isEditingInfo && (
                <>
                  <div className="fpp-edit-form-group">
                    <label>Username</label>
                    <input
                      type="text"
                      name="username"
                      value={editFormData.username || ""}
                      onChange={(e) => {
                        const value = e.target.value
                          .toLowerCase()
                          .replace(/\s/g, "");
                        handleEditInfoChange({
                          ...e,
                          target: { ...e.target, name: "username", value },
                        });
                      }}
                      className="fpp-input"
                      disabled={savingInfo}
                      maxLength={30}
                    />
                  </div>
                  <input
                    type="text"
                    name="name"
                    value={editFormData.name}
                    onChange={handleEditInfoChange}
                    className="fpp-input fpp-input-name"
                    placeholder="Full name"
                  />
                </>
              )}
            </div>
            <div className="fpp-identity-actions">
              {isOwnProfile && (
                <>
                  <Link
                    to="/farmer/dashboard"
                    className="fpp-btn fpp-btn-ghost"
                  >
                    {t("farmerPublicProfile.dashboard")}
                  </Link>
                  <Link to="/farmer/payments" className="fpp-btn fpp-btn-ghost">
                    <FaCoins /> {t("farmerPublicProfile.payments")}
                  </Link>
                  <button
                    type="button"
                    className={`fpp-btn ${isEditingInfo ? "fpp-btn-danger" : "fpp-btn-primary"}`}
                    onClick={() =>
                      isEditingInfo
                        ? handleCancelInfoEdit()
                        : setIsEditingInfo(true)
                    }
                  >
                    <FaEdit />{" "}
                    {isEditingInfo
                      ? "Cancel"
                      : t("farmerPublicProfile.editProfile")}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Stats row */}
        <div className="fpp-stats">
          <div className="fpp-stat">
            <FaBox className="fpp-stat-icon" />
            <span className="fpp-stat-value">{farmer.totalProducts}</span>
            <span className="fpp-stat-label">
              {t("farmerPublicProfile.products")}
            </span>
          </div>
          <div className="fpp-stat">
            <FaClipboardList className="fpp-stat-icon" />
            <span className="fpp-stat-value">{farmer.totalOrders}</span>
            <span className="fpp-stat-label">
              {t("farmerPublicProfile.orders")}
            </span>
          </div>
          <div className="fpp-stat">
            <FaStar className="fpp-stat-icon" />
            <span className="fpp-stat-value">{farmer.rating}</span>
            <span className="fpp-stat-label">
              {t("farmerPublicProfile.rating")}
            </span>
          </div>
          <div className="fpp-stat">
            <FaCalendarAlt className="fpp-stat-icon" />
            <span className="fpp-stat-value">
              {farmer.joinedDate
                ? new Date(farmer.joinedDate).getFullYear()
                : "—"}
            </span>
            <span className="fpp-stat-label">
              {t("farmerPublicProfile.memberSince")}
            </span>
          </div>
        </div>

        {isOwnProfile && (
          <div className="fpp-tabs">
            <button
              type="button"
              className={`fpp-tab-btn ${activeTab === "profile" ? "active" : ""}`}
              onClick={() => setActiveTab("profile")}
            >
              {t("farmerPublicProfile.profile")}
            </button>
            <button
              type="button"
              className={`fpp-tab-btn ${activeTab === "settings" ? "active" : ""}`}
              onClick={() => setActiveTab("settings")}
            >
              {t("farmerPublicProfile.settings")}
            </button>
          </div>
        )}

        {activeTab === "settings" && isOwnProfile ? (
          <div className="fpp-card fpp-settings-card">
            <h2 className="fpp-card-title">
              {t("farmerPublicProfile.accountSettings")}
            </h2>
            <div className="fpp-settings-danger">
              <h3>{t("farmerPublicProfile.backupDeleteAccount")}</h3>
              <p className="fpp-settings-hint">
                {t("farmerPublicProfile.backupHint")}
              </p>
              <div className="fpp-settings-actions">
                <button
                  type="button"
                  className="fpp-btn fpp-btn-backup"
                  onClick={handleDownloadBackup}
                  disabled={exportingData}
                >
                  {exportingData
                    ? "Preparing…"
                    : t("farmerPublicProfile.downloadMyData")}
                </button>
                <button
                  type="button"
                  className="fpp-btn fpp-btn-backup-drive"
                  onClick={handleBackupToGoogleDrive}
                  disabled={exportingData}
                >
                  {exportingData
                    ? "Preparing…"
                    : t("farmerPublicProfile.backupToGoogleDrive")}
                </button>
                <button
                  type="button"
                  className="fpp-btn fpp-btn-delete-account"
                  onClick={handleDeleteAccount}
                  disabled={deletingAccount}
                >
                  {deletingAccount
                    ? "Deleting…"
                    : t("farmerPublicProfile.deleteMyAccount")}
                </button>
              </div>
            </div>
          </div>
        ) : (
          <>
            <div className="fpp-grid">
              {/* Location - This address is used as your location in orders, products, and marketplace */}
              <div className="fpp-card">
                <h3 className="fpp-card-title">
                  <FaMapMarkerAlt /> {t("farmerPublicProfile.location")}
                </h3>
                {isEditingInfo ? (
                  <div className="fpp-edit-form-group">
                    <p className="fpp-location-hint">
                      {t("farmerPublicProfile.locationHint")}
                    </p>
                    <div className="fpp-location-detect-row">
                      <button
                        type="button"
                        className="fpp-btn fpp-btn-ghost fpp-btn-location"
                        onClick={handleUseMyLocation}
                        disabled={detectingLocation}
                      >
                        <FaMapMarkerAlt />
                        {detectingLocation
                          ? t("farmerPublicProfile.detecting")
                          : t("farmerPublicProfile.useMyLocation")}
                      </button>
                    </div>
                    <label>City</label>
                    <input
                      type="text"
                      name="city"
                      value={editFormData.city}
                      onChange={handleEditInfoChange}
                      className="fpp-input"
                      placeholder="e.g. Kathmandu"
                    />
                    <label>Province</label>
                    <select
                      name="province"
                      value={editFormData.province}
                      onChange={handleEditInfoChange}
                      className="fpp-input"
                    >
                      <option value="">Select Province</option>
                      {nepaleseProvinces.map((p) => (
                        <option key={p} value={p}>
                          {p}
                        </option>
                      ))}
                    </select>
                    <label>District</label>
                    <select
                      name="district"
                      value={editFormData.district}
                      onChange={handleEditInfoChange}
                      className="fpp-input"
                      disabled={!editFormData.province}
                    >
                      <option value="">Select District</option>
                      {editFormData.province &&
                        (districtsByProvince[editFormData.province] || []).map(
                          (d) => (
                            <option key={d} value={d}>
                              {d}
                            </option>
                          ),
                        )}
                    </select>
                    <label>Municipality</label>
                    <input
                      type="text"
                      name="municipality"
                      value={editFormData.municipality}
                      onChange={handleEditInfoChange}
                      className="fpp-input"
                      placeholder="e.g. Kathmandu Metropolitan City"
                    />
                    <label>Street / Address</label>
                    <input
                      type="text"
                      name="address"
                      value={editFormData.address}
                      onChange={handleEditInfoChange}
                      className="fpp-input"
                      placeholder="Street or area"
                    />
                  </div>
                ) : (
                  <>
                    <p className="fpp-text-primary">
                      {[
                        farmer.address?.city,
                        farmer.district || farmer.address?.district,
                        farmer.province || farmer.address?.province,
                      ]
                        .filter(Boolean)
                        .join(", ") || "—"}
                    </p>
                    <p className="fpp-text-muted">
                      {farmer.address?.street || farmer.address || "—"}
                    </p>
                  </>
                )}
              </div>

              {/* Contact */}
              <div className="fpp-card">
                <h3 className="fpp-card-title">
                  <FaPhone /> {t("farmerPublicProfile.contact")}
                </h3>
                {isEditingInfo ? (
                  <div className="fpp-edit-form-group">
                    <label>Phone</label>
                    <input
                      type="text"
                      name="phone"
                      value={editFormData.phone}
                      onChange={handleEditInfoChange}
                      className="fpp-input"
                    />
                    <label>Email</label>
                    <input
                      type="email"
                      name="email"
                      value={editFormData.email}
                      onChange={handleEditInfoChange}
                      className="fpp-input"
                    />
                  </div>
                ) : (
                  <>
                    <p className="fpp-contact-line">
                      <FaPhone /> {farmer.phone || "—"}
                    </p>
                    <p className="fpp-contact-line">
                      <FaEnvelope /> {farmer.email}
                    </p>
                  </>
                )}
              </div>

              {/* Farm & payment (view + edit) */}
              {isOwnProfile && (
                <div className="fpp-card fpp-card-full">
                  <h3 className="fpp-card-title">
                    <FaStore /> {t("farmerPublicProfile.farmPayment")}
                  </h3>
                  {isEditingInfo ? (
                    <div className="fpp-edit-form-group">
                      <label>{t("farmerPublicProfile.farmBusinessType")}</label>
                      <div className="fpp-radio-group">
                        <label className="fpp-radio">
                          <input
                            type="radio"
                            name="farmerType"
                            value="sellingFromHome"
                            checked={
                              editFormData.farmerType === "sellingFromHome"
                            }
                            onChange={handleEditInfoChange}
                          />
                          {t("farmerPublicProfile.sellingFromHome")}
                        </label>
                        <label className="fpp-radio">
                          <input
                            type="radio"
                            name="farmerType"
                            value="registeredFarm"
                            checked={
                              editFormData.farmerType === "registeredFarm"
                            }
                            onChange={handleEditInfoChange}
                          />
                          {t("farmerPublicProfile.registeredFarm")}
                        </label>
                      </div>
                      {editFormData.farmerType === "registeredFarm" && (
                        <>
                          <label>{t("farmerPublicProfile.farmName")}</label>
                          <input
                            type="text"
                            name="farmName"
                            value={editFormData.farmName}
                            onChange={handleEditInfoChange}
                            className="fpp-input"
                            placeholder="Enter farm name"
                          />
                          <label>{t("farmerPublicProfile.farmPhoto")}</label>
                          <div className="fpp-farm-photo-row">
                            <input
                              type="file"
                              accept="image/*"
                              onChange={handleFarmPhotoChange}
                              className="fpp-input-file"
                            />
                            {(farmPhotoPreview || farmer.farmPhoto) && (
                              <div className="fpp-farm-photo-preview">
                                <img
                                  src={
                                    farmPhotoPreview ||
                                    getImageUrl(farmer.farmPhoto)
                                  }
                                  alt="Farm"
                                />
                              </div>
                            )}
                          </div>
                        </>
                      )}
                      <label>{t("farmerPublicProfile.paymentMethod")}</label>
                      <select
                        name="paymentMethod"
                        value={editFormData.paymentMethod}
                        onChange={handleEditInfoChange}
                        className="fpp-input"
                      >
                        <option value="">Select</option>
                        <option value="Bank">Bank transfer</option>
                        <option value="khalti">Khalti</option>
                        <option value="esewa">eSewa</option>
                      </select>
                      {editFormData.paymentMethod === "khalti" && (
                        <>
                          <label>Khalti account type</label>
                          <select
                            name="khaltiAccountType"
                            value={editFormData.khaltiAccountType}
                            onChange={handleEditInfoChange}
                            className="fpp-input"
                          >
                            <option value="">Select</option>
                            <option value="personal">Personal</option>
                            <option value="merchant">Merchant</option>
                          </select>
                          <label>Khalti account ID / mobile</label>
                          <input
                            type="text"
                            name="khaltiAccountId"
                            value={editFormData.khaltiAccountId}
                            onChange={handleEditInfoChange}
                            className="fpp-input"
                            placeholder="e.g. 98XXXXXXXX"
                          />
                        </>
                      )}
                    </div>
                  ) : (
                    <div className="fpp-farm-payment-view">
                      <p>
                        <strong>Type:</strong>{" "}
                        {farmer.farmerType === "registeredFarm"
                          ? "Registered farm"
                          : "Selling from home"}
                      </p>
                      {farmer.farmerType === "registeredFarm" &&
                        farmer.farmName && (
                          <p>
                            <strong>Farm:</strong> {farmer.farmName}
                          </p>
                        )}
                      {farmer.farmerType === "registeredFarm" &&
                        (farmer.farmPhoto || farmPhotoPreview) && (
                          <div className="fpp-farm-photo-preview fpp-farm-photo-view">
                            <img
                              src={
                                farmPhotoPreview ||
                                getImageUrl(farmer.farmPhoto)
                              }
                              alt="Farm"
                            />
                          </div>
                        )}
                      <p>
                        <strong>Payment:</strong>{" "}
                        {farmer.paymentMethod || "Not set"}
                        {farmer.paymentMethod === "khalti" &&
                        farmer.khaltiAccountId
                          ? ` (${farmer.khaltiAccountType || "—"} · ${farmer.khaltiAccountId})`
                          : ""}
                      </p>
                    </div>
                  )}
                </div>
              )}

              {isEditingInfo && (
                <div className="fpp-card fpp-card-full fpp-edit-actions">
                  <button
                    type="button"
                    onClick={handleSaveInfo}
                    disabled={savingInfo}
                    className="fpp-btn fpp-btn-primary fpp-btn-lg"
                  >
                    {savingInfo
                      ? t("farmerPublicProfile.saving")
                      : t("farmerPublicProfile.saveChanges")}
                  </button>
                  <button
                    type="button"
                    onClick={handleCancelInfoEdit}
                    disabled={savingInfo}
                    className="fpp-btn fpp-btn-ghost fpp-btn-lg"
                  >
                    Cancel
                  </button>
                </div>
              )}
            </div>

            {/* About & Specialties */}
            <div className="fpp-card fpp-about">
              <h3 className="fpp-card-title">
                <FaLeaf /> {t("farmerPublicProfile.about")}
              </h3>
              {isEditingInfo ? (
                <textarea
                  name="bio"
                  value={editFormData.bio}
                  onChange={handleEditInfoChange}
                  className="fpp-textarea"
                  rows={5}
                  placeholder={t("farmerPublicProfile.bioPlaceholder")}
                />
              ) : (
                <p className="fpp-bio">
                  {farmer.bio || t("farmerPublicProfile.noBioAdded")}
                </p>
              )}
              <div className="fpp-specialties">
                <h4 className="fpp-specialties-title">
                  {t("farmerPublicProfile.specialties")}
                </h4>
                {isEditingInfo ? (
                  <div className="fpp-specialty-editor">
                    <div className="fpp-specialty-tags">
                      {(editFormData.specialties || []).map((s, i) => (
                        <span
                          key={i}
                          className="fpp-specialty-tag fpp-specialty-tag--editable"
                        >
                          {s}
                          <button
                            type="button"
                            className="fpp-specialty-remove"
                            onClick={() =>
                              setEditFormData((prev) => ({
                                ...prev,
                                specialties: prev.specialties.filter(
                                  (_, idx) => idx !== i,
                                ),
                              }))
                            }
                            aria-label={`Remove ${s}`}
                          >
                            &times;
                          </button>
                        </span>
                      ))}
                    </div>
                    <div className="fpp-specialty-input-row">
                      <input
                        type="text"
                        className="fpp-input fpp-specialty-input"
                        value={specialtyInput}
                        onChange={(e) => setSpecialtyInput(e.target.value)}
                        onKeyDown={(e) => {
                          if (
                            (e.key === "Enter" || e.key === ",") &&
                            specialtyInput.trim()
                          ) {
                            e.preventDefault();
                            const val = specialtyInput.trim();
                            if (
                              !(editFormData.specialties || []).some(
                                (s) => s.toLowerCase() === val.toLowerCase(),
                              )
                            ) {
                              setEditFormData((prev) => ({
                                ...prev,
                                specialties: [...(prev.specialties || []), val],
                              }));
                            }
                            setSpecialtyInput("");
                          }
                        }}
                        placeholder={t(
                          "farmerPublicProfile.specialtyPlaceholder",
                        )}
                        disabled={savingInfo}
                      />
                      <button
                        type="button"
                        className="fpp-btn fpp-btn-ghost fpp-specialty-add-btn"
                        disabled={!specialtyInput.trim() || savingInfo}
                        onClick={() => {
                          const val = specialtyInput.trim();
                          if (
                            val &&
                            !(editFormData.specialties || []).some(
                              (s) => s.toLowerCase() === val.toLowerCase(),
                            )
                          ) {
                            setEditFormData((prev) => ({
                              ...prev,
                              specialties: [...(prev.specialties || []), val],
                            }));
                          }
                          setSpecialtyInput("");
                        }}
                      >
                        {t("farmerPublicProfile.addSpecialty")}
                      </button>
                    </div>
                    <p className="fpp-specialty-hint">
                      {t("farmerPublicProfile.specialtyHint")}
                    </p>
                  </div>
                ) : (
                  <div className="fpp-specialty-tags">
                    {specialties.length ? (
                      specialties.map((s, i) => (
                        <span key={i} className="fpp-specialty-tag">
                          {s}
                        </span>
                      ))
                    ) : (
                      <span className="fpp-text-muted">
                        {t("farmerPublicProfile.noSpecialties")}
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Products */}
            {farmer.verified || isOwnProfile ? (
              <div className="fpp-card fpp-products">
                <div className="fpp-products-head">
                  <h3 className="fpp-card-title">
                    {t("farmerPublicProfile.productsCount", {
                      count: products.length,
                    })}
                  </h3>
                  <Link
                    to={`/market?farmer=${farmer._id}`}
                    className="fpp-link"
                  >
                    {t("farmerPublicProfile.viewAll")}
                  </Link>
                </div>
                <div className="fpp-products-grid">
                  {products.map((product) => (
                    <ProductCard
                      key={product._id}
                      product={product}
                      user={user}
                      onAddToCart={handleAddToCart}
                    />
                  ))}
                </div>
              </div>
            ) : (
              <div className="fpp-card fpp-validation-msg">
                <h3>{t("farmerPublicProfile.adminValidationPending")}</h3>
                <p>
                  {t("farmerPublicProfile.productsVisibleAfterVerification")}
                </p>
              </div>
            )}
          </>
        )}

        <ProfileImageCropModal
          imageSrc={cropImageSrc}
          open={cropModalOpen}
          onSave={handleCropSave}
          onCancel={handleCropCancel}
        />
      </div>

      {user?.role === "consumer" && (
        <>
          <FloatingCart
            itemCount={cartItems.reduce((s, i) => s + i.quantity, 0)}
            onClick={() => setIsCartOpen(true)}
          />
          <CartDrawer
            isOpen={isCartOpen}
            onClose={() => setIsCartOpen(false)}
            cartItems={cartItems}
            setCartItems={setCartItems}
          />
        </>
      )}
    </div>
  );
};

export default FarmerPublicProfile;
